/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Launch ads", description = "Queue finished ads, choose the destination campaign, and launch them to your connected ad platforms.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Launch ads",
        description: "Queue finished ads, choose the destination campaign, and launch them to your connected ad platforms.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Queue ads for launch",
          "depth": 0,
          "id": "queue-ads-for-launch"
        }, {
          "value": "Select a campaign",
          "depth": 0,
          "id": "select-a-campaign"
        }, {
          "value": "Configure and launch",
          "depth": 0,
          "id": "configure-and-launch"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: "A finished ad"
                }), _jsxs("li", {
                  children: ["At least one connected ad platform in Context Hub's ", _jsx(_components.a, {
                    href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
                    children: "Connected data"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Launch sends a finished ad to the platform you connect. Queue the ads you want, choose the destination campaign, fill in the platform fields, and launch."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "queue-ads-for-launch",
        children: "Queue ads for launch"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-launch-selected.png",
          alt: "Launch queue showing queued and launched ads"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Queue for launch"
        }), " in the ", _jsx(_components.a, {
          href: "/ad-studio/refine#favorite-queue-and-export-ads",
          children: "toolbar"
        }), " to add an ad to the queue. Then open ", _jsx(_components.strong, {
          children: "Launch"
        }), " in the sidebar, select the ads you want, and select ", _jsx(_components.strong, {
          children: "Launch ads"
        }), ". The queue spans every chat, so you can launch ads from different chats together."]
      }), "\n", _jsxs(_components.p, {
        children: ["Launch tracks two states: ", _jsx(_components.strong, {
          children: "Queued"
        }), " shows ads waiting to go out with their platform, status, and date, and ", _jsx(_components.strong, {
          children: "Launched"
        }), " shows the ones already sent."]
      }), "\n", _jsx(_components.h2, {
        id: "select-a-campaign",
        children: "Select a campaign"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Select the destination ad platform."
        }), "\n", _jsx(_components.li, {
          children: "Select the ad account, page, campaign, and ad set."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Refresh from Meta"
        }), ", or the equivalent for your platform, to pull the latest campaign structure."]
      }), "\n", _jsx(_components.h2, {
        id: "configure-and-launch",
        children: "Configure and launch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Fill in the platform fields for each ad: ad name, headlines, primary texts, descriptions, calls to action, destination URL, and URL parameters. Required fields and character limits vary by platform."
        }), "\n", _jsx(_components.li, {
          children: "Select the aspect ratios you want to launch."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Launch 1 ad"
          }), ", or the equivalent for your selection."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The ad arrives in your ad platform as a new creative asset. You manage targeting, budget, and scheduling in the ad platform itself."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/figma",
            children: "Hand off to Figma: Bring a finished ad into Figma for final layout"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/insights",
            children: "Insights: Find fatiguing creatives and competitor activity"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
